import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Box,
  CircularProgress,
  FormControlLabel,
  Grid,
  Typography,
} from "@mui/material";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import React, { useEffect, useMemo } from "react";
import ColorWheel from "./ColorWheel";
import useFilters from "../hooks/useFilters";

function normalizeFiltersForParent(obj) {
  if (!obj || typeof obj !== "object") return {};
  const out = {};
  Object.keys(obj).forEach((key) => {
    if (key === "searching") {
      const s = obj[key];
      if (s != null && String(s).trim() !== "") out[key] = String(s).trim();
      return;
    }
    const val = obj[key];
    if (Array.isArray(val) && val.length > 0) out[key] = val;
  });
  return out;
}

const colorMap = {
  White: "#F9F9F7",
  Ivory: "#F4EFE6",
  Champagne: "#E6D3A3",
  Taupe: "#A79B90",
  Gold: "linear-gradient(135deg, #C9A227, #FFD700, #FFF4B0, #FFD700, #C9A227)",
  Yellow: "#E5C15A",
  Copper: "#B46A3C",
  Brown: "#5A3A2E",
  "Burnt Orange": "#C65A1E",
  Orange: "#F07A2F",
  Peach: "#F6B7A9",
  Coral: "#F06A6A",
  Pink: "#E8A0A6",
  Fuchsia: "#C72C7B",
  Red: "#A61E2A",
  Green: "#2F6B4F",
  Turquoise: "#5FAFAD",
  Blue: "#3F6F95",
  Purple: "#6B4A6E",
  Silver:
    "linear-gradient(135deg, #A6A6A6, #E6E6E6, #FFFFFF, #E6E6E6, #A6A6A6)",
  Gray: "#6D6E71",
  Black: "#1C1C1C",
};

const COLOR_ORDER = Object.keys(colorMap);
const colorOrderIndex = COLOR_ORDER.reduce((acc, name, i) => {
  acc[name.toLowerCase().replace(/\s+/g, "")] = i;
  return acc;
}, {});

const normalizeColorLabel = (value = "") =>
  String(value)
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ");

const formatColorLabel = (value = "") =>
  normalizeColorLabel(value)
    .split(" ")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

const getColorHex = (colorName) => {
  const norm = normalizeColorLabel(colorName);
  const matchedKey = Object.keys(colorMap).find(
    (key) => normalizeColorLabel(key) === norm
  );
  return matchedKey ? colorMap[matchedKey] : "#CCCCCC";
};

const colorRank = (label) => {
  const key = normalizeColorLabel(label).replace(/\s+/g, "");
  return colorOrderIndex[key] ?? Number.MAX_SAFE_INTEGER;
};

export default function Filters({
  selectedCategory,
  filters = {},
  onFiltersChange,
  filterPanelOpen = false,
  selectedSort = "",
  onSortChange = () => {},
}) {
  const { data: filtersResponse, isLoading: isLoadingFilters } = useFilters(
    selectedCategory,
    { enabled: !!filterPanelOpen }
  );

  const { sortGroup, filterGroups } = useMemo(() => {
    const raw = filtersResponse?.filters ?? filtersResponse ?? [];
    let groups = [];
    let sort = null;

    if (Array.isArray(raw)) {
      raw.forEach((group) => {
        const slug = group?.slug || "";
        const requestKey =
          group?.request_key ||
          (slug === "color" ? "colors" : slug || group?.name);
        const options = Array.isArray(group?.options)
          ? group.options
              .filter(
                (o) =>
                  String(o?.label || "").toLowerCase() !== "all" &&
                  String(o?.value || "").toLowerCase() !== "all"
              )
              .map((o) => ({
                label: o?.label || o?.value || "",
                value: o?.value || o?.label || "",
              }))
              .filter((o) => o.label && o.value)
          : [];

        if (slug === "sort" || requestKey === "sort") {
          if (options.length > 0) {
            sort = {
              requestKey: requestKey || "sort",
              defaultValue: group?.default || "",
              options,
            };
          }
          return;
        }

        if (requestKey && options.length > 0) {
          groups.push({
            title: group?.name || slug || requestKey,
            requestKey,
            slug,
            options,
          });
        }
      });
    } else if (raw && typeof raw === "object") {
      Object.keys(raw).forEach((key) => {
        const config = raw[key];
        const values = Array.isArray(config?.filters) ? config.filters : [];
        const requestKey = key === "color" ? "colors" : key;
        const options = values
          .filter((v) => String(v).toLowerCase() !== "all")
          .map((v) => ({ label: String(v).trim(), value: String(v).trim() }))
          .filter((o) => o.label);

        if (key === "sort" || config?.slug === "sort") {
          if (options.length > 0) {
            sort = {
              requestKey: "sort",
              defaultValue: config?.default || "",
              options,
            };
          }
          return;
        }

        if (requestKey && options.length > 0) {
          groups.push({
            title: config?.name || key,
            requestKey,
            slug: config?.slug || key,
            options,
          });
        }
      });
    }

    const decorated = groups.map((g) => {
      const isColor = g.requestKey === "colors" || g.slug === "color";
      if (!isColor) return g;
      return {
        ...g,
        options: [...g.options].sort(
          (a, b) => colorRank(a.label) - colorRank(b.label)
        ),
      };
    });

    return { sortGroup: sort, filterGroups: decorated };
  }, [filtersResponse]);

  useEffect(() => {
    if (!sortGroup) return;
    if (selectedSort) return;
    if (sortGroup.defaultValue) onSortChange(sortGroup.defaultValue);
  }, [sortGroup, selectedSort, onSortChange]);

  const handleFilters = (requestKey, value) => {
    const current = Array.isArray(filters[requestKey])
      ? filters[requestKey]
      : [];
    const idx = current.indexOf(value);
    const next =
      idx === -1
        ? [...current, value]
        : current.filter((item) => item !== value);
    onFiltersChange(
      normalizeFiltersForParent({ ...filters, [requestKey]: next })
    );
  };

  return (
    <Grid
      sx={{
        height: "100%",
        maxHeight: "calc(100vh - 105px)",
        overflowY: "auto",
      }}>
      <Grid item px={2}>
        <Box
          sx={{
            display: "none",
            background: "#e7e7e7",
            position: "relative",
            height: "170px",
          }}>
          <ColorWheel onChange={(key, value) => handleFilters(key, value)} />
        </Box>
        {isLoadingFilters ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "200px",
            }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Box sx={{ height: "100%", overflowY: "auto" }}>
            {sortGroup && sortGroup.options.length > 0 && (
              <Accordion
                sx={{
                  boxShadow: "none",
                  borderBottom: "1px solid #E0E0E0",
                  "&:before": { display: "none" },
                  "&.Mui-expanded": { margin: 0 },
                }}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel-sort-content"
                  id="panel-sort-header">
                  <Typography className="filter-head"> Sort By</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {sortGroup.options.map((option, idx) => {
                    const activeValue =
                      selectedSort || sortGroup.defaultValue || "";
                    const isChecked = activeValue === option.value;
                    return (
                      <Box
                        key={`${option.value}-${idx}`}
                        alignItems="center"
                        gap={1}
                        display="flex"
                        mb={1}
                        width="100%">
                        <FormControlLabel
                          sx={{ margin: 0 }}
                          control={
                            <input
                              type="radio"
                              name="sort"
                              checked={isChecked}
                              onChange={() => onSortChange(option.value)}
                            />
                          }
                        />
                        <Typography
                          className="filter-label"
                          key={option.value}>
                          {option.label}
                        </Typography>
                      </Box>
                    );
                  })}
                </AccordionDetails>
              </Accordion>
            )}
            {filterGroups.length > 0 &&
              filterGroups.map((group, index) => (
                <Accordion
                  key={index}
                  sx={{
                    boxShadow: "none",
                    borderBottom: "1px solid #E0E0E0",
                    "&:before": { display: "none" },
                    "&.Mui-expanded": { margin: 0 },
                  }}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel3-content"
                    id="panel3-header">
                    <Typography className="filter-head">
                      {" "}
                      {group.title}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    {group.requestKey === "colors" ||
                    group.slug === "color" ? (
                      <Grid container spacing={2}>
                        {group.options.map((option, idx) => {
                          const isSelected = filters[group.requestKey]?.includes(
                            option.value
                          );
                          return (
                            <Grid item xs={2} key={`${option.value}-${idx}`}>
                              <Box
                                onClick={() =>
                                  handleFilters(group.requestKey, option.value)
                                }
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  cursor: "pointer",
                                  position: "relative",
                                }}>
                                <Box
                                  sx={{
                                    borderRadius: "50%",
                                    border: isSelected
                                      ? "1.1px solid #39302A"
                                      : "1.1px solid transparent",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    mb: 1,
                                    position: "relative",
                                    aspectRatio: "1/1",
                                    padding: "4px",
                                  }}>
                                  <Box
                                    sx={{
                                      width: "33px",
                                      height: "33px",
                                      aspectRatio: "1/1",
                                      borderRadius: "50%",
                                      background: getColorHex(option.label),
                                      border: "1.1px solid #E0E0E0",
                                    }}
                                  />
                                </Box>
                                <Typography
                                  className="filter-label"
                                  sx={{
                                    fontSize: "12px",
                                    textAlign: "center",
                                    mt: 0.5,
                                  }}>
                                  {formatColorLabel(option.label)}
                                </Typography>
                              </Box>
                            </Grid>
                          );
                        })}
                      </Grid>
                    ) : (
                      group.options.map((option, idx) => (
                        <Box
                          key={`${option.value}-${idx}`}
                          alignItems="center"
                          gap={1}
                          display="flex"
                          mb={1}
                          width="100%">
                          <FormControlLabel
                            sx={{ margin: 0 }}
                            control={
                              <input
                                type="checkbox"
                                checked={
                                  filters[group.requestKey]?.includes(
                                    option.value
                                  ) || false
                                }
                                onChange={() =>
                                  handleFilters(group.requestKey, option.value)
                                }
                              />
                            }
                          />
                          <Typography
                            className="filter-label"
                            key={option.value}>
                            {option.label}
                          </Typography>
                        </Box>
                      ))
                    )}
                  </AccordionDetails>
                </Accordion>
              ))}
          </Box>
        )}
      </Grid>
    </Grid>
  );
}
